import {
  LanguageEnum,
  AssignmentStatusEnum,
  IUser,
  ServiceStatusEnum,
  LessonAttempt,
  ILesson,
  IAssessment
} from "@imaginelearning/mps-api-service";
import {
  UxAvatarEnum,
  UxAvatarPriorityEnum,
  UxAvatarStyleEnum,
  UxIconEnum,
  UxIconLibraryEnum,
  UxPriorityEnum,
  UxShadesEnum,
  UxSizeEnum,
  UxSizeExtendedEnum,
  UxSizeExtraEnum,
  UxStateEnum,
  UxStatusEnum,
  UxStyleEnum,
  classMap
} from "@imaginelearning/mps-ux-component-library";
import AppWindow from "helpers/app-window";
import defaultLoadingActionProp from "../../../shared/default-action";

export interface IAssignment {
  isNext: boolean;
  complete: boolean;
  title: string;
  isReset: boolean;
  language: LanguageEnum;
  status: AssignmentStatusEnum;
  spanishTitle: string;
  pathId: number;
  renderGuidedNotes: any;
  type?: string;
}

export interface getChildrenFromAssignmentsProps {
  assignments: IAssignment[];
  ilpSubject: string;
  user: IUser;
  isKto2: boolean;
  is6to12: boolean;
  is6to8: boolean;
  onClick: Function;
  getIcon: (subjectName: string) => any;
  theme: any;
  translator: any;
  languageOverride?: string;
}

const smallLoadingActionProp = {
  ...defaultLoadingActionProp,
  size: UxSizeEnum.SMALL as any
};

export const getChildrenFromAssignments = ({
  assignments,
  ilpSubject,
  user,
  is6to12,
  isKto2,
  getIcon,
  onClick,
  is6to8,
  theme,
  translator,
  languageOverride
}: getChildrenFromAssignmentsProps) => {
  const isMobileDevice = window.innerWidth <= 767;

  if (assignments.some((lesson) => lesson.type === "Tier2")) {
    preLoadDoolittle();
  }

  return assignments.map((assignment: IAssignment) => {
    const isCurrent: boolean = assignment.isNext;
    const isComplete: boolean = !isCurrent && assignment.complete;
    const actionIcon: UxIconEnum = isComplete ? UxIconEnum.CHECK_CIRCLE : UxIconEnum.LOCK;
    const currentPriority: UxPriorityEnum = isCurrent ? UxPriorityEnum.SUCCESS : UxPriorityEnum.SECONDARY;
    const currentState: UxStateEnum = isCurrent ? UxStateEnum.INTERACTIVE : UxStateEnum.LOCKED;
    const state: UxStateEnum = isComplete ? UxStateEnum.COMPLETE : currentState;
    let lessonDisplayTitle: string = assignment.title;

    let isSpanishContent = false;
    const isSpanishSubject = ilpSubject.toLowerCase() === "math" || ilpSubject.toLowerCase() === "science";
    if (assignment.type === "Tier1") {
      if (languageOverride === "es" && assignment.spanishTitle) {
        lessonDisplayTitle = assignment.spanishTitle;
        isSpanishContent = true;
      }
    } else if (isSpanishSubject) {
      const hasSpanishAttempt = !assignment.isReset && assignment.language === LanguageEnum.Spanish;
      const isNewAttempt = !(isComplete || assignment.status === AssignmentStatusEnum.IN_PROGRESS);
      // Do not delete - We will be reusing this logic when spanish content is honored by student settings"
      const shouldLaunchInSpanish =
        isNewAttempt && user.spanishLanguage.includes(ilpSubject.toLowerCase()) && assignment.spanishTitle;
      if (hasSpanishAttempt || shouldLaunchInSpanish) {
        lessonDisplayTitle = assignment.spanishTitle;
        isSpanishContent = true;
      }
    }

    const getButtonText = () => {
      const text = is6to12 ? "Start" : "GO";
      if (assignment.type === "assessment") {
        return text;
      }
      return translator(text);
    };

    const currentIcon = is6to12
      ? {
          icon: UxIconEnum.MAP_MARKER_ALT,
          library: UxIconLibraryEnum.REGULAR,
          size: UxSizeExtendedEnum.X2,
          className: classMap("current-icon text-white bg-blue rounded-circle p-2"),
          squared: true,
          theme
        }
      : {
          icon: getIcon(ilpSubject),
          size: UxSizeExtendedEnum.X2,
          className: isKto2 && ilpSubject === "Science" ? "abld_science_icon_white" : "",
          theme
        };

    const nonCurrentIcon = {
      icon: UxIconEnum.CHECK,
      size: UxSizeExtendedEnum.X1,
      className: classMap(
        "completed-icon text-light rounded-circle p-2 border-gray",
        !isComplete ? "bg-light" : "bg-success"
      ),
      squared: true,
      theme
    };

    let icon;
    if (isCurrent) {
      icon = currentIcon;
    } else if (is6to12) {
      icon = nonCurrentIcon;
    }

    let size;
    let priority;
    let borderClass = "animation border-ease";
    let bodyClass = "animation align-after border-ease";
    let marginClass = "mx-auto";
    if (is6to12) {
      borderClass = isCurrent ? "border-primary" : "border-white";
      bodyClass = "m-0 mw-100";
      marginClass = "p-3 py-4";
    } else {
      size = isCurrent ? UxSizeExtraEnum.XL : UxSizeEnum.LARGE;
      priority = UxShadesEnum.LIGHT;
    }

    let actionText;
    if (isCurrent) {
      actionText = getButtonText();
    }

    let statusClass = "locked";
    if (isCurrent) {
      statusClass = "current";
    } else if (isComplete) {
      statusClass = "completed";
    }

    return {
      ...smallLoadingActionProp,
      key: assignment.pathId,
      size,
      priority,
      content: {
        state,
        icon,
        className: classMap(
          "align-items-center",
          is6to12 ? "justify-content-between" : "flex-column justify-content-center"
        ),
        title: lessonDisplayTitle,
        titleClassName: classMap(
          is6to12 && isCurrent ? "text-xl left-text" : "text-lg",
          is6to12 && isComplete ? "text-white" : ""
        ),
        meta: assignment.renderGuidedNotes ?? undefined,
        action: {
          enabled: isCurrent,
          priority: isComplete || is6to12 ? UxPriorityEnum.PRIMARY : currentPriority,
          text: actionText,
          callback: () => {
            onClick({ ...assignment, isSpanishContent });
          },
          style: is6to12 ? undefined : UxStyleEnum.ROUNDED,
          tabIndex: 0,
          className: classMap(
            "shadow-sm mt-3 start-btn",
            isCurrent ? "px-5" : "",
            isMobileDevice ? "mobile-device" : "",
            is6to12 ? "" : "mx-auto"
          ),
          icon: {
            icon: isCurrent ? UxIconEnum.ARROW_RIGHT : actionIcon,
            size: isComplete ? UxSizeExtendedEnum.X2 : UxSizeExtendedEnum.X1,
            className: is6to12 ? "d-none" : "",
            theme
          }
        },
        ...(is6to12 && !isCurrent ? { action: undefined } : {}),
        ...(is6to8 && isCurrent
          ? {
              avatar: {
                className: "active-avatar",
                avatar: user.selectedAvatar as UxAvatarEnum,
                avatarType: UxAvatarPriorityEnum.DEFAULT,
                size: UxSizeExtendedEnum.X3,
                avatarStyle: UxAvatarStyleEnum.BODY,
                rotation: 90,
                theme
              }
            }
          : {})
      },
      status: UxStatusEnum.LOADED,
      state,
      className: classMap("lesson-card", borderClass, statusClass, marginClass),
      bodyClassName: classMap(bodyClass),
      focused: isCurrent,
      leftAlign: true,
      showOverflow: true
    };
  });
};

export const getContainerChildren = (status: string, loadedCallback: () => any) => {
  let children;
  const errorActionProp = {
    ...smallLoadingActionProp,
    status: UxStatusEnum.ERROR
  };

  switch (status) {
    case ServiceStatusEnum.ERROR:
      children = [
        { ...errorActionProp },
        { ...errorActionProp, size: UxSizeEnum.MEDIUM as any },
        { ...errorActionProp }
      ];
      break;
    case ServiceStatusEnum.LOADING:
      children = [
        { ...smallLoadingActionProp },
        { ...smallLoadingActionProp, size: UxSizeEnum.MEDIUM as any },
        { ...smallLoadingActionProp }
      ];
      break;
    case ServiceStatusEnum.LOADED:
      children = loadedCallback();
      if (!children?.length) {
        children = [
          { ...errorActionProp },
          { ...errorActionProp, size: UxSizeEnum.MEDIUM as any },
          { ...errorActionProp }
        ];
      }
      break;
    default:
      break;
  }
  return children;
};

export const preLoadDoolittle = () => {
  if (document.getElementById("doolittle-reader.js") === null) {
    const appWindow = AppWindow() as any;

    const doolittleBaseMediaUrl = appWindow.STUDENT_ENV.REACT_APP_DOOLITTLE_READER_BASE_URL;
    const doolittleVersion = appWindow.STUDENT_ENV.REACT_APP_DOOLITTLE_READER_VERSION;
    const script = document.createElement("script");
    script.id = "doolittle-reader.js";
    script.src = `${doolittleBaseMediaUrl}/static/reader/${doolittleVersion}/reader.${doolittleVersion}.js`;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      const reader = script;
      if (reader) {
        reader.className = "ready";
        document.dispatchEvent(new CustomEvent("doolittle-reader-ready"));
      }
    };

    script.onerror = () => {
      document.getElementById("doolittle-reader.js")?.remove();
    };
    document.body.appendChild(script);
  }
};

export const unloadDoolittle = () => {
  const doolittleScript = document.getElementById("doolittle-reader.js");
  if (doolittleScript) {
    doolittleScript.remove();
    document.dispatchEvent(new CustomEvent("doolittle-reader-unloaded"));
  }
};

export const getLessonProgress = (lessonAttempts: LessonAttempt[], lessons: ILesson[]) => {
  if (lessonAttempts.length === 0) return 0;
  const lessonCount = lessons.length;
  const lessonsCompleted = lessonAttempts.filter((lesson: LessonAttempt) => lesson.isCompleted).length;
  const percentComplete = (lessonsCompleted / lessonCount) * 100;
  return { lessonCount, lessonsCompleted, percentComplete };
};
export const assignmentProgess = (
  contentType: number,
  lessonAttempts: LessonAttempt[],
  lessons: ILesson[],
  assessments: IAssessment[]
) => {
  if (contentType === 0) {
    const progress = getLessonProgress(lessonAttempts, lessons);
    if (progress) {
      return progress.percentComplete;
    }
    return 0;
  }
  return (
    (assessments.filter((item: any) => item.status === AssignmentStatusEnum.COMPLETED).length / assessments.length) *
    100
  );
};
